import { Bus, type SoundBusManager } from '@src/audio/SoundBusManager';

const MUSIC_PATH = '/assets/music';
/** Sample amplitude under which the head/tail of a file counts as encoder padding. */
const SILENCE = 0.004;
const DEFAULT_FADE = 1.6;
const DEFAULT_GAIN = 0.8;

export type TrackId = 'menu' | 'floor_obsidian' | 'floor_verdant' | 'floor_infernal' | 'boss';

export const FLOOR_TRACKS: readonly TrackId[] = ['floor_obsidian', 'floor_verdant', 'floor_infernal'];

interface Voice {
  id: TrackId;
  src: AudioBufferSourceNode;
  gain: GainNode;
}

interface Loaded {
  buffer: AudioBuffer;
  loopStart: number;
  loopEnd: number;
}

/**
 * Streamed soundtrack layer: decoded AudioBuffers looping on the Music bus so the
 * announcer sidechain ducks them like the procedural engine. Files that are
 * missing (or fail to decode) simply report `has() === false`; callers keep the
 * procedural MusicEngine for those. Loop points trim encoder silence so a
 * composed-to-loop track wraps without a gap.
 */
export class MusicTracks {
  private readonly ctx: AudioContext;
  private readonly out: GainNode;
  private readonly cache = new Map<TrackId, Loaded | null>();
  private readonly loading = new Map<TrackId, Promise<Loaded | null>>();
  private current: Voice | null = null;
  /** Voices already faded off `current` but still ringing out. */
  private readonly fading: Voice[] = [];

  constructor(buses: SoundBusManager) {
    this.ctx = buses.context;
    this.out = this.ctx.createGain();
    this.out.gain.value = DEFAULT_GAIN;
    this.out.connect(buses.bus(Bus.Music));
  }

  /** Kick off decoding without playing; `has()` flips to true when it lands. */
  preload(ids: Iterable<TrackId>): void {
    for (const id of ids) void this.load(id);
  }

  /** True once the file is decoded and playable. */
  has(id: TrackId): boolean {
    return !!this.cache.get(id);
  }

  /** Resolves when the file is known to be present or missing. */
  ready(id: TrackId): Promise<boolean> {
    return this.load(id).then((l) => !!l);
  }

  /** Id of the track currently sounding (or fading in), if any. */
  playing(): TrackId | null {
    return this.current?.id ?? null;
  }

  /**
   * Crossfade to `id`. Returns false (and leaves the current track alone) when the
   * file is not decoded yet; call again later or await `ready()` first.
   */
  play(id: TrackId, fade = DEFAULT_FADE, volume = 1): boolean {
    const loaded = this.cache.get(id);
    if (!loaded) return false;
    if (this.current?.id === id) return true;
    const t = this.ctx.currentTime;
    this.fadeOutCurrent(t, fade);

    const src = this.ctx.createBufferSource();
    src.buffer = loaded.buffer;
    src.loop = true;
    src.loopStart = loaded.loopStart;
    src.loopEnd = loaded.loopEnd;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), t + Math.max(0.05, fade));
    src.connect(gain);
    gain.connect(this.out);
    src.start(t, loaded.loopStart);
    this.current = { id, src, gain };
    return true;
  }

  /** Fade every sounding loop out, including leftovers from a prior crossfade. */
  stop(fade = DEFAULT_FADE): void {
    const t = this.ctx.currentTime;
    const voices = this.fading.splice(0);
    if (this.current) {
      voices.push(this.current);
      this.current = null;
    }
    const kill = Math.min(0.22, fade);
    for (const v of voices) this.rampOut(v, t, kill);
  }

  /** Master level for the streamed layer (0..1). */
  setLevel(level: number, seconds = 0.4): void {
    this.out.gain.setTargetAtTime(Math.max(0.0001, level * DEFAULT_GAIN), this.ctx.currentTime, seconds);
  }

  destroy(): void {
    this.stop(0.05);
  }

  private fadeOutCurrent(t: number, fade: number): void {
    const v = this.current;
    if (!v) return;
    this.current = null;
    this.rampOut(v, t, fade);
    this.fading.push(v);
  }

  private rampOut(v: Voice, t: number, fade: number): void {
    const g = v.gain.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(Math.max(0.0001, g.value), t);
    g.exponentialRampToValueAtTime(0.0001, t + Math.max(0.05, fade));
    try {
      v.src.stop(t + fade + 0.1);
    } catch {
      // already stopped
    }
  }

  private load(id: TrackId): Promise<Loaded | null> {
    const cached = this.cache.get(id);
    if (cached !== undefined) return Promise.resolve(cached);
    const inflight = this.loading.get(id);
    if (inflight) return inflight;
    const p = (async (): Promise<Loaded | null> => {
      try {
        const res = await fetch(`${MUSIC_PATH}/${id}.mp3`);
        if (!res.ok) return null;
        // Dev servers answer unknown paths with index.html; refuse anything that is not audio.
        const type = res.headers.get('content-type') ?? '';
        if (type.includes('text/html')) return null;
        const data = await res.arrayBuffer();
        if (data.byteLength < 4096) return null;
        const buffer = await this.ctx.decodeAudioData(data);
        return { buffer, ...findLoop(buffer) };
      } catch {
        return null;
      }
    })();
    this.loading.set(id, p);
    p.then((loaded) => {
      this.cache.set(id, loaded);
      this.loading.delete(id);
    });
    return p;
  }
}

/** Trim leading/trailing near-silence (MP3 encoder padding) so the loop wraps cleanly. */
function findLoop(buffer: AudioBuffer): { loopStart: number; loopEnd: number } {
  const ch = buffer.getChannelData(0);
  const n = ch.length;
  let start = 0;
  while (start < n && Math.abs(ch[start]) < SILENCE) start++;
  let end = n - 1;
  while (end > start && Math.abs(ch[end]) < SILENCE) end--;
  // Keep a hair of the tail so the last transient is not clipped.
  const loopStart = start / buffer.sampleRate;
  const loopEnd = Math.min(buffer.duration, (end + 1) / buffer.sampleRate);
  if (loopEnd - loopStart < 1) return { loopStart: 0, loopEnd: buffer.duration };
  return { loopStart, loopEnd };
}
